// Where to send a visitor after they sign in: the page they started from.
//
// The last non-auth page is kept in sessionStorage as the visitor navigates
// (see ReturnPathTracker), which survives the reload after email sign-in and the
// round trip to Google. functions/lib/returnPath.ts applies the same rules on the server.

const KEY = 'cc-return-to';

// Pages of the sign-in flow itself: returning to one of these after signing in
// would just loop, and they are never somewhere to "start from".
const AUTH_PATHS = new Set(['/login', '/welcome', '/reset-password', '/oauth/consent']);

export function isAuthPath(pathname: string): boolean {
  return AUTH_PATHS.has(pathname);
}

/** A same-site path we are willing to navigate to: no other origins, no scheme-relative or backslash tricks, no sign-in pages. */
export function isSafeReturnPath(path: string | null | undefined): path is string {
  if (!path || path.length > 300 || path[0] !== '/' || path[1] === '/' || path.includes('\\')) return false;
  // eslint-disable-next-line no-control-regex -- rejecting control characters is the point
  if (/[\u0000-\u001f\u007f]/.test(path)) return false;
  try {
    const url = new URL(path, window.location.origin);
    return url.origin === window.location.origin && !isAuthPath(url.pathname) && !url.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

export function rememberReturnPath(path: string): void {
  try {
    sessionStorage.setItem(KEY, path);
  } catch {
    // Storage can be blocked (private mode); signing in then lands on the fallback.
  }
}

/** The page to return to after signing in, or `fallback` when none was recorded. */
export function getReturnPath(fallback = '/'): string {
  try {
    const stored = sessionStorage.getItem(KEY);
    if (isSafeReturnPath(stored)) return stored;
  } catch {
    // see rememberReturnPath
  }
  return fallback;
}
